'use client';

import { Alert, FormControlLabel, Grid, Switch, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import React, { useEffect, useState } from 'react';

import { dayjs } from '../../utils';
import { EditorModal } from '../modals/editorModal';

export const ParkerTerminationModal = ({
	handleClose,
	handleSave,
	effectiveDate,
	setEffectiveDate,
	terminationNoticeDay,
	terminationRule,
	theme,
	containerClassName = '',
	loading = false,
	showManualAdjustmentAlert = false,
}) => {
	const [optOutOfTerminationAdjustment, setOptOutOfTerminationAdjustment] = useState(false);
	// the reason we have this hook is because this modal gets rendered inside of a dialog, and
	// when the prop effectiveDate changes, the modal doesn't re render. this hook
	// is just so when the user changes the date input in this modal, the associated calculations
	// also update.
	const [effectiveDateCopy, setEffectiveDateCopy] = useState(effectiveDate);

	useEffect(() => {
		setEffectiveDate(effectiveDateCopy);
	}, [effectiveDateCopy, setEffectiveDate]);

	const terminationRuleToReadableValue = {
		DAILY_PRORATION: 'Daily Proration',
		HALF_MONTH: 'Half Month Proration',
	};
	const dayOfMonth = dayjs(effectiveDateCopy).date();
	const daysInMonth = dayjs(effectiveDateCopy).daysInMonth();
	const billingProfileIsHalfMonth =
		terminationRuleToReadableValue[terminationRule] === terminationRuleToReadableValue.HALF_MONTH;

	const getProrationPercentage = () => {
		// assume the termination rule for the subscription is daily if it's not half month
		let percentage = dayOfMonth / daysInMonth;
		if (billingProfileIsHalfMonth) {
			// 50% discount if on or before the termination notice day, otherwise no proration
			percentage = dayjs(effectiveDateCopy).date() <= terminationNoticeDay ? 0.5 : 1;
		}
		const percentageToTwoDecimalPlaces = Math.floor(percentage * 10000) / 100;
		return percentageToTwoDecimalPlaces;
	};

	return (
		<EditorModal
			handleClose={handleClose}
			handleSave={() => handleSave(optOutOfTerminationAdjustment, effectiveDateCopy)}
			headerText='Termination'
			hasUnsavedChanges={false}
			loading={loading}
			containerClassName={containerClassName}
			theme={theme}
		>
			<FormControlLabel
				control={
					<Switch
						color='primary'
						name='showInactiveDevices'
						onChange={(e) => setOptOutOfTerminationAdjustment(e.target.checked)}
					/>
				}
				label='Opt out of termination adjustment'
			/>
			<Grid container direction='column'>
				<Grid container direction='row'>
					<Grid item xs>
						<Typography variant='h6'>Termination Date</Typography>
					</Grid>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<Grid item xs>
							<DatePicker
								label='Effective Date'
								value={effectiveDateCopy}
								onChange={(newValue: string | null) => setEffectiveDateCopy(newValue || null)}
								slotProps={{
									openPickerIcon: {
										'data-cy': 'date-picker-open-icon',
									},
								}}
							/>
						</Grid>
					</LocalizationProvider>
				</Grid>
				{!optOutOfTerminationAdjustment && (
					<>
						<Grid container direction='row'>
							<Grid item xs>
								<Typography variant='h6'>Termination Rule</Typography>
							</Grid>
							<Grid item xs>
								{terminationRuleToReadableValue[terminationRule]}
							</Grid>
						</Grid>
						<Grid container direction='row'>
							<Grid item xs>
								<Typography variant='h6'>Number of days parked</Typography>
							</Grid>
							<Grid item xs>
								{dayOfMonth}
							</Grid>
						</Grid>
						<Grid container direction='row'>
							<Grid item xs>
								<Typography variant='h6'>Number of days in month</Typography>
							</Grid>
							<Grid item xs>
								{daysInMonth}
							</Grid>
						</Grid>
						{billingProfileIsHalfMonth && (
							<Grid container direction='row'>
								<Grid item xs>
									<Typography variant='h6'>Subscription Termination Notice Day</Typography>
								</Grid>
								<Grid item xs>
									{terminationNoticeDay}
								</Grid>
							</Grid>
						)}
						<Grid container direction='row'>
							<Grid item xs>
								<Typography variant='h6'>Percentage of month paid for</Typography>
							</Grid>
							<Grid item xs>
								{getProrationPercentage()} %
							</Grid>
						</Grid>
					</>
				)}

				{showManualAdjustmentAlert && (
					<Alert severity='warning'>
						An existing invoice has been prorated by the Parker&apos;s original termination date. This
						invoice must be manually adjusted to undo the proration adjustments.
					</Alert>
				)}
			</Grid>
		</EditorModal>
	);
};
